import {MultiFilePicker} from '@github-ui/copilot-spaces/components/MultiFilePicker'
import type {GitHubFileFormData} from '@github-ui/copilot-spaces/types'
import {fetchRepo} from '@github-ui/copilot-spaces/utils/fetch-repo'
import {useCallback, useRef} from 'react'

import {makeFileReference, makeFolderReference} from '../utils/copilot-chat-helpers'
import type {CopilotChatRepo} from '../utils/copilot-chat-types'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'

interface CopilotFilePickerProps {
  isOpen: boolean
  onClose: () => void
  initialRepo?: CopilotChatRepo | null
  onRepoSelect?: (repo: CopilotChatRepo | null) => void
}

export function CopilotFilePicker({isOpen, onClose, initialRepo, onRepoSelect}: CopilotFilePickerProps) {
  const manager = useChatManager()
  const {findFileWorkerPath} = useChatState()
  const attachmentButtonRef = useRef<HTMLButtonElement>(null)

  const handleSaveFiles = useCallback(
    async (formData: GitHubFileFormData[]) => {
      if (formData.length === 0) {
        onClose()
        return
      }

      // since the files can only come from one repo at a time, we only need to fetch one repository
      const repo = await fetchRepo(formData[0]!.repositoryId)

      const references = formData.map(item => {
        if (item.type === 'github_folder') {
          return makeFolderReference(item.filePath, repo)
        } else {
          return makeFileReference(item.filePath, repo)
        }
      })

      for (const reference of references) {
        manager.addReference(reference, 'references-menu')
      }

      onClose()
    },
    [manager, onClose],
  )

  if (!isOpen) {
    return null
  }

  return (
    <MultiFilePicker
      formData={[]}
      onCancel={onClose}
      onSaveFiles={handleSaveFiles}
      findFileWorkerPath={findFileWorkerPath}
      attachmentButtonRef={attachmentButtonRef}
      initialRepo={initialRepo}
      onRepoSelect={onRepoSelect}
      allowFolderReferences
      maxFileCount={20}
    />
  )
}
